import type { FilterConfig } from "@/lib/types/data.types";
import { DEFAULT_OPERATORS, OPERATOR_SYMBOL_MAP } from "./constants";
import type { FilterColumnDef, FilterOperatorDef } from "./types";

export function getOperatorsForColumn(
  column: FilterColumnDef
): FilterOperatorDef[] {
  return column.operators ?? DEFAULT_OPERATORS[column.type] ?? [];
}

export function getColumnDef(
  columns: FilterColumnDef[],
  key: string
): FilterColumnDef | undefined {
  return columns.find((c) => c.key === key);
}

export function getDefaultFilterColumn(
  columns: FilterColumnDef[],
  defaultColumnKey?: string,
): FilterColumnDef | undefined {
  return (
    columns.find((column) => column.key === defaultColumnKey)
    ?? columns[0]
  );
}

export function getDisplayLabel(
  columns: FilterColumnDef[],
  filter: FilterConfig
): { column: string; operator: string; value: string } {
  const col = getColumnDef(columns, filter.column);
  const columnLabel = col?.label ?? filter.column;

  const operators = col ? getOperatorsForColumn(col) : [];
  const operatorLabel =
    operators.find((o) => o.value === filter.operator)?.label ??
    OPERATOR_SYMBOL_MAP[filter.operator] ??
    filter.operator;

  const valueLabel =
    col?.options?.find((o) => o.value === String(filter.value))?.label ??
    String(filter.value);

  return { column: columnLabel, operator: operatorLabel, value: valueLabel };
}

export function getOperatorSymbol(operator: string): string | undefined {
  return OPERATOR_SYMBOL_MAP[operator];
}

export function formatNumberForDisplay(input: string): string {
  if (input === "") return "";
  const sign = input.startsWith("-") ? "-" : "";
  const sanitized = input.replace(/,/g, "").replace(/^-/, "");
  const [intPartRaw, decPart] = sanitized.split(".");
  const intPart = intPartRaw ?? "";
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart !== undefined
    ? `${sign}${withCommas}.${decPart}`
    : `${sign}${withCommas}`;
}

export function toPascalSpaced(text: string): string {
  return text
    .split("_")
    .map((part) => (part ? part[0]?.toUpperCase() + part.slice(1) : part))
    .join(" ");
}
